import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

export interface XlsxSheet {
  sheetName: string;
  rows: Record<string, any>[];
}

/**
 * Utility service for exporting tabular data to CSV or multi-sheet xlsx files.
 *
 * CSV notes:
 * - Values containing commas or quotes are double-quoted and internal quotes are escaped.
 * - Use this service's toCsv() for single-table exports from each sub-tab.
 *
 * XLSX notes:
 * - Requires the `xlsx` (SheetJS) package.
 * - Use toXlsx() for the full class report (multiple sheets).
 */
@Injectable({ providedIn: 'root' })
export class ExportService {

  /**
   * Exports an array of plain objects as a UTF-8 CSV file download.
   * Column headers are derived from the keys of the first row.
   *
   * @param rows     Array of data objects (must share the same keys).
   * @param fileName Desired file name, without the `.csv` extension.
   */
  toCsv(rows: Record<string, any>[], fileName: string): void {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const escape = (val: unknown): string => {
      const str = val == null ? '' : String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    const lines = [
      headers.map(escape).join(','),
      ...rows.map(row => headers.map(h => escape(row[h])).join(',')),
    ];
    this.downloadBlob(lines.join('\r\n'), `${fileName}.csv`, 'text/csv;charset=utf-8;');
  }

  /**
   * Exports an array of sheet definitions as a multi-sheet xlsx file download.
   * Empty-row sheets are included as blank sheets (so the workbook always has every tab).
   *
   * @param sheets   Array of { sheetName, rows } descriptors.
   * @param fileName Desired file name, without the `.xlsx` extension.
   */
  toXlsx(sheets: XlsxSheet[], fileName: string): void {
    const workbook = XLSX.utils.book_new();
    for (const { sheetName, rows } of sheets) {
      const worksheet = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([[]]);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }

  private downloadBlob(content: string, fileName: string, mimeType: string): void {
    const bom = '\uFEFF'; // BOM for Excel UTF-8 compatibility
    const blob = new Blob([bom + content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }
}
